import React from "react";
import { NavLink, Link } from "react-router-dom";
import { Home, Compass, Radio, Film, MonitorPlay, Star, Users, Bookmark, History as HistoryIcon, Settings, Wifi } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * PlayerSidebar — left rail used on every Watch page (VOD + Live).
 *
 * Mirrors the layout from the user's reference mockup: vertical nav with
 * a "LIVE" badge on Live TV, a "GO LIVE. GET DISCOVERED. WIN BIG." promo
 * card, and a small user-profile snapshot at the bottom.
 *
 * Hidden below `lg` to keep the player full-bleed on phones — the existing
 * page already uses `max-w-[1400px] mx-auto` so reserving 224px on the
 * left at desktop fits inside that frame.
 */

const ITEMS = [
  { to: "/",              icon: Home,        label: "Home",     end: true,  testid: "psb-home" },
  { to: "/browse",        icon: Compass,                        label: "Explore", testid: "psb-explore" },
  { to: "/live",          icon: Radio,                          label: "Live TV", badge: "LIVE", testid: "psb-live" },
  { to: "/browse/films",  icon: Film,                           label: "Movies",  testid: "psb-movies" },
  { to: "/tv-guide",      icon: MonitorPlay,                    label: "TV Shows", testid: "psb-shows" },
  { to: "/originals",     icon: Star,                           label: "Originals", testid: "psb-originals" },
  { to: "/social/explore",icon: Users,                          label: "Creators", testid: "psb-creators" },
  { to: "/my-list",       icon: Bookmark,                       label: "My List",  testid: "psb-mylist" },
  { to: "/history",       icon: HistoryIcon,                    label: "History",  testid: "psb-history" },
  { to: "/settings",      icon: Settings,                       label: "Settings", testid: "psb-settings" },
];

export default function PlayerSidebar() {
  const { user } = useAuth();
  return (
    <aside
      data-testid="player-sidebar"
      className="hidden lg:flex flex-col gap-3 w-[224px] shrink-0 self-start sticky top-24"
    >
      {/* Nav rail */}
      <nav className="bg-black/50 border border-white/10 rounded-2xl p-2 backdrop-blur">
        {ITEMS.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={!!it.end}
              data-testid={it.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition ${isActive ? "bg-red-500/15 text-white border-l-4 border-red-500" : "text-white/80 hover:bg-white/5 hover:text-white border-l-4 border-transparent"}`
              }
            >
              <Icon className="w-5 h-5 shrink-0" />
              <span className="truncate">{it.label}</span>
              {it.badge && (
                <span className="ml-auto bg-red-500 text-white text-[9px] font-black uppercase px-1.5 py-0.5 rounded">{it.badge}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Go Live promo card */}
      <div data-testid="psb-go-live-card" className="rounded-2xl border-2 border-red-500/60 bg-gradient-to-br from-red-500/10 via-black to-black p-4">
        <div className="text-red-500 font-black text-sm tracking-wider">GO LIVE.</div>
        <div className="font-black text-white text-sm tracking-wider mt-0.5">GET DISCOVERED.</div>
        <div className="font-black text-white text-sm tracking-wider">WIN BIG.</div>
        <Link
          to="/studio/live"
          data-testid="psb-go-live-now"
          className="mt-3 w-full bg-red-500 hover:bg-red-600 text-white font-black text-xs py-2.5 rounded-lg inline-flex items-center justify-center gap-1.5 transition"
        ><Wifi className="w-3.5 h-3.5" /> Go Live Now</Link>
      </div>

      {/* User profile snapshot */}
      {user && (
        <Link
          to="/dashboard"
          data-testid="psb-profile-card"
          className="rounded-2xl bg-black/50 border border-white/10 p-3 flex items-center gap-3 hover:bg-white/5 transition"
        >
          <div className="w-12 h-12 rounded-full ring-2 ring-red-500 overflow-hidden shrink-0">
            {user.picture
              ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full bg-gradient-to-br from-red-500 to-rose-700 flex items-center justify-center font-black">{(user.name || "?")[0]}</div>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold text-sm truncate">{user.name}</div>
            <div className="text-white/50 text-[11px] truncate">@{user.handle || user.email?.split("@")[0]}</div>
          </div>
        </Link>
      )}
    </aside>
  );
}
